import { UserRole, OrganizationRole } from '@/types/enums';

interface User {
  id: string
  email: string
  name?: string
  role: UserRole
  globalRole?: UserRole
  orgRole?: OrganizationRole
  parentId?: string
  adminId?: string
  currentOrganizationId?: string
  availableOrganizations?: Array<{
    id: string
    organizationId: string
    organizationName: string
    organizationSlug: string
    role: OrganizationRole
    subscriptionTier?: string
  }>
}

interface SessionData {
  token: string
  user: User
  expiresAt: number
}

const SESSION_KEY = 'football_tracker_session'
const TOKEN_KEY = 'token' // Keep for backward compatibility

export const sessionStorage = {
  /**
   * Save complete session data including user info and expiration
   */
  saveSession(token: string, user: User, expiresIn: number = 24 * 60 * 60 * 1000): void {
    const sessionData: SessionData = {
      token,
      user,
      expiresAt: Date.now() + expiresIn
    }
    
    localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData))
    localStorage.setItem(TOKEN_KEY, token) // Backward compatibility
  },

  /**
   * Get session data if valid, null if expired or invalid
   */
  getSession(): SessionData | null {
    try {
      const sessionJson = localStorage.getItem(SESSION_KEY)
      if (!sessionJson) {
        // Try legacy token storage
        const token = localStorage.getItem(TOKEN_KEY)
        return token ? { token, user: null as any, expiresAt: Date.now() + 24 * 60 * 60 * 1000 } : null
      }

      const sessionData: SessionData = JSON.parse(sessionJson)
      
      // Check if session is expired
      if (Date.now() > sessionData.expiresAt) {
        this.clearSession()
        return null
      }

      return sessionData
    } catch (error) {
      console.warn('Failed to parse session data:', error)
      this.clearSession()
      return null
    }
  },

  /**
   * Get just the token if session is valid
   */
  getToken(): string | null {
    const session = this.getSession()
    return session?.token || null
  },

  /**
   * Get user data if session is valid
   */
  getUser(): User | null {
    const session = this.getSession()
    return session?.user || null
  },

  /**
   * Check if current session is valid (not expired)
   */
  isSessionValid(): boolean {
    const session = this.getSession()
    return session !== null
  },

  /**
   * Clear all session data
   */
  clearSession(): void {
    localStorage.removeItem(SESSION_KEY)
    localStorage.removeItem(TOKEN_KEY)
  },

  /**
   * Update session expiration time (extend session)
   */
  extendSession(additionalTime: number = 24 * 60 * 60 * 1000): void {
    const session = this.getSession()
    if (session) {
      session.expiresAt = Date.now() + additionalTime
      localStorage.setItem(SESSION_KEY, JSON.stringify(session))
    }
  },

  /**
   * Get time until session expires in milliseconds
   */
  getTimeUntilExpiry(): number {
    const session = this.getSession()
    if (!session) return 0
    return Math.max(0, session.expiresAt - Date.now())
  }
}
